import { useEffect, useRef, useState } from "react";
import { Info, Minimize2, X } from "lucide-react";
import "@/components/SmallTattooModal.css";

const SMALL_TATTOO_IMAGE = "/small-tattoo-example.webp";

export default function SmallTattooModal({ open, onClose, onRequest, triggerRef }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const [showAiInfo, setShowAiInfo] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const triggerNode = triggerRef?.current;
    document.body.style.overflow = "hidden";
    setShowAiInfo(false);
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 0);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
        )
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      window.setTimeout(() => triggerNode?.focus(), 0);
    };
  }, [open, onClose, triggerRef]);

  if (!open) return null;

  const handleRequest = () => {
    onClose();
    window.setTimeout(onRequest, 0);
  };

  return (
    <div className="small-tattoo-modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section
        ref={dialogRef}
        className="small-tattoo-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="small-tattoo-modal-title"
      >
        <button ref={closeRef} type="button" className="small-tattoo-modal-close" onClick={onClose} aria-label="Fenster schließen">
          <X size={20} />
        </button>

        <div className="small-tattoo-modal-copy">
          <span className="small-tattoo-modal-kicker">Kleine Variante</span>
          <h2 id="small-tattoo-modal-title">Klein, klar und auf das Wesentliche reduziert.</h2>
          <div className="small-tattoo-price">150–300 €</div>
          <p>
            Auch aus deiner Auswahl kann eine kleinere Tattoo-Version entstehen. Dafür reduziere ich Details, vereinfache Formen und passe die Komposition so an, dass sie auch in kleiner Größe klar und gut lesbar bleibt.
          </p>
          <p>
            Die genaue Ausarbeitung hängt vom Motiv, der gewünschten Größe und der Körperstelle ab. Nicht jede Kombination lässt sich gleich stark verkleinern – ich passe sie so an, dass sie als Tattoo funktioniert.
          </p>

          <button
            type="button"
            className="small-tattoo-ai-trigger"
            onClick={() => setShowAiInfo((value) => !value)}
            aria-expanded={showAiInfo}
            aria-controls="small-tattoo-ai-info"
          >
            <Info size={15} /> Hinweis zum Beispielbild
          </button>

          {showAiInfo ? (
            <div id="small-tattoo-ai-info" className="small-tattoo-ai-info">
              <strong>Transparenz:</strong> Dieses Beispielbild ist KI-generiert und zeigt nur, wie eine reduzierte Oldschool-Ausarbeitung aussehen könnte. Es ist keine fertige Vorlage und wird nicht 1:1 übernommen. Deine Auswahl wird individuell angepasst und für Größe, Körperstelle und Tattoo-Umsetzung neu ausgearbeitet.
            </div>
          ) : null}
        </div>

        <div className="small-tattoo-example-card">
          <img src={SMALL_TATTOO_IMAGE} alt="KI-generiertes Beispiel für kleine reduzierte Oldschool-Tattoo-Varianten" />
          <div>
            <strong>So könnten kleine Varianten aussehen.</strong>
            <span>Beispiel für Stil, Reduktion und mögliche Ausarbeitung.</span>
          </div>
        </div>

        <div className="small-tattoo-modal-actions">
          <button type="button" className="small-tattoo-request-cta" onClick={handleRequest}>
            <Minimize2 size={18} /> Kleine Variante anfragen
          </button>
        </div>
      </section>
    </div>
  );
}
